import React, { useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Link, MenuItem, Paper, Select, SelectChangeEvent, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import { DataPlanMap, DataPlans, InfegyUser, SeatTypeMap, SeatTypes, UserPreference, currentUser, permissionTest, permissionsTest } from "system/InfegyUser";
import { setDocumentTitle } from "system/Utilities";
import TopBar from "../components/dashboardParts/TopBar";
import UserBanner from "components/profilePage/UserBanner";
import LanguageAutocomplete, { LanguageOption } from "components/uiElements/inputs/LanguageAutocomplete";
import TimezoneAutocomplete from "components/uiElements/inputs/TimezoneAutocomplete";
import ChangePassword from "components/profilePage/ChangePassword";
import ManageApiTokens from "components/profilePage/ManageApiTokens";
import APIRequest, { APIError } from "system/APIRequest";
import { useSnackbar } from "notistack";
import ThemeAutocomplete from "components/uiElements/inputs/ThemeAutocomplete";
import { ThemeContext } from "theme/ThemeProvider";
import TwoFactorAuthAutocomplete from "components/uiElements/inputs/TwoFactorAuthAutocomplete";
import DefaultQuickSearchVsAISelect from "components/uiElements/inputs/DefaultQuickSearchVsAISelect";
import SkipConvertingQuickSearchSelect from "components/uiElements/inputs/SkipConvertingQuickSearchSelect";
import TitleCase from "components/formatting/TitleCase";

export enum Homepages {
  QUICK_SEARCH = "quick-search",
  ADVANCED = "start",
  VIEWER = "viewer-home",
  API = "api-home",
  AI = "pulse-ai"
}

export const HomepageLabels = {
  [Homepages.QUICK_SEARCH]: "Quick Search",
  [Homepages.ADVANCED]: "Advanced",
  [Homepages.VIEWER]: "View Only",
  [Homepages.API]: "API",
  [Homepages.AI]: "Pulse AI"
};

export function getDefaultHomepageForRole(userRole: SeatTypes): Homepages {
  let homepage = Homepages.ADVANCED;
  if (userRole === SeatTypes.QUICK_SEARCH) {
    homepage = Homepages.QUICK_SEARCH;
  } else if (userRole === SeatTypes.API_ONLY) {
    homepage = Homepages.API;
  } else if (userRole === SeatTypes.PULSE_AI) {
    homepage = Homepages.AI;
  } else if (userRole === SeatTypes.VIEW_ONLY) {
    homepage = Homepages.VIEWER;
  }
  return homepage;
}

export function getAvailableHomepagesForUser(user: InfegyUser): Homepages[] {
  let availableHomepages: Homepages[] = [];
  const userRole = user?.role;
  if (userRole === SeatTypes.QUICK_SEARCH) {
    availableHomepages = [Homepages.QUICK_SEARCH, Homepages.ADVANCED, Homepages.VIEWER];
  } else if (userRole === SeatTypes.API_ONLY) {
    availableHomepages = [Homepages.API];
  } else if (userRole === SeatTypes.PULSE_AI) {
    availableHomepages = [Homepages.AI];
  } else if (userRole === SeatTypes.VIEW_ONLY) {
    availableHomepages = [Homepages.VIEWER];
  } else {
    availableHomepages = [Homepages.ADVANCED, Homepages.QUICK_SEARCH, Homepages.VIEWER];
  }
  if (permissionTest('ai_features') && availableHomepages.length > 1) {
    availableHomepages.push(Homepages.AI);
  }
  return availableHomepages;
}

export default function ProfilePage(): React.JSX.Element {
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const setThemeName = useContext(ThemeContext);
  const user = currentUser() as InfegyUser;

  const [isChangePasswordExpanded, setChangePasswordExpanded] = useState(false);
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);

  const [defaultHomepage, setDefaultHomepage] = useState(user.getPreference(UserPreference.DEFAULT_HOMEPAGE) || "auto");

  const lockoutDuration = 500;
  const lockoutTime = useRef(0);

  const hasApiPermission = user?.permissions?.includes("api");

  useEffect(() => {
    if (user?.getPreference(UserPreference.DEFAULT_QUICKSEARCH_VS_AI) === "on" ||
        user?.getPreference(UserPreference.SKIP_CONVERTING_QUICKSEARCH_BOOLEAN) === "on") {
      setAdvancedOptionsOpen(true);
    }
  }, [user?.preferences]);

  const handleToggleChangePasswordExpanded = () => {
    const now = new Date().getTime();
    if (now > (lockoutTime.current + lockoutDuration)) {
      setChangePasswordExpanded((current) => !current);
      lockoutTime.current = now;
    }
  };

  async function handleThemeChange(theme: string): Promise<void> {
    user.setPreference(UserPreference.THEME, theme);
    await user.savePreferences();
    setThemeName(theme);
    enqueueSnackbar(i18n.t("profile-page.theme-saved"), { variant: "success" });
  }

  async function handleLanguageChange(language: LanguageOption) {
    user.setPreference(UserPreference.LOCALE, language.locale);
    await user.savePreferences();
    i18n.changeLanguage(language.code);
    enqueueSnackbar(i18n.t("profile-page.language-saved"), { variant: "success" });
  }

  async function handleTimeZoneChange(timeZone: string) {
    user.setPreference(UserPreference.TIME_ZONE, timeZone);
    await user.savePreferences();
    enqueueSnackbar(i18n.t("profile-page.time-zone-saved"), { variant: "success" });
  }

  async function handleTwoFactorChange(method: string) {
    user.second_factor = method;
    await APIRequest(`obj/user/${user.id}`, {}, {
      method: "PUT",
      body: JSON.stringify({'second_factor': method})
    });
    enqueueSnackbar(i18n.t("profile-page.two-factor-saved"), { variant: "success" });
  }

  async function handleDefaultQuickSearchVsAIChange(value: string | null) {
    user.setPreference(UserPreference.DEFAULT_QUICKSEARCH_VS_AI, value === "on" ? "on" : "off");
    await user.savePreferences();
    enqueueSnackbar(i18n.t("profile-page.default-quicksearch-vs-ai-saved"), { variant: "success" });
  }

  async function handleSkipConvertingQuickSearchChange(value: string | null) {
    user.setPreference(UserPreference.SKIP_CONVERTING_QUICKSEARCH_BOOLEAN, value === "on" ? "on" : "off");
    await user.savePreferences();
    enqueueSnackbar(i18n.t("profile-page.skip-converting-quicksearch-boolean-saved"), { variant: "success" });
  }

  function handleChangePasswordClose() {
    setChangePasswordExpanded(false);
  }

  function handleChangePasswordSuccess() {
    setChangePasswordExpanded(false);
    enqueueSnackbar(i18n.t("change-password.change-password-success"), { variant: "success" });
  }

  useEffect(() => {
    setDocumentTitle(i18n.t("profile-page.document-title"));
  }, []);

  // The current advanced options features are only applicable to some roles
  const hasAdvancedOptions = ["quick-search", "analyst"].includes(user.role);
  const hasMatchingRole = !!SeatTypeMap[user.role as SeatTypes];

  const availableHomePages = getAvailableHomepagesForUser(user);
  const homepageOptions = availableHomePages.map((homepage) => {
    return (<MenuItem key={homepage} value={homepage}>
      {HomepageLabels[homepage]}
    </MenuItem>);
  });
  homepageOptions.unshift(<MenuItem key="auto" value="auto">{i18n.t("auto")}</MenuItem>);

  return (
    <Box sx={{ maxWidth:"1300px", margin:"0 auto", position:"relative", pl: 2, pr: 2 }}>
      <TopBar title={i18n.t('home.my-account')} showLogo={true}></TopBar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center"
        }}>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            padding: 2,
            width: 700,
            maxWidth: 700
          }}>
          <Paper sx={{ padding: 2, width: "100%"}}>
            <UserBanner user={user}></UserBanner>
          </Paper>
          <Paper sx={{ width: "100%"}}>
            <Stack
              direction="row"
              sx={{
                padding: 2,
                justifyContent: "space-between"
              }}>
              <Stack>
                <Typography variant="h3" sx={{
                  fontSize: "1.5em"
                }}>{i18n.t("user-account-info.role")}</Typography>
                {hasMatchingRole &&
                  <Box>{i18n.t(SeatTypeMap[user.role as SeatTypes])}</Box>
                  ||
                  <TitleCase text={user.role} />
                }
              </Stack>
              <Stack>
                <Typography variant="h3" sx={{
                  fontSize: "1.5em"
                }}>{i18n.t("user-account-info.plan")}</Typography>
                <Box>{i18n.t(DataPlanMap[user.account.plan as DataPlans])}</Box>
              </Stack>
              {user.account.social_plan &&
                <Stack>
                  <Typography variant="h3" sx={{
                    fontSize: "1.5em"
                  }}>{i18n.t("user-account-info.social-plan")}</Typography>
                  <Box>{i18n.t(DataPlanMap[user.account.social_plan as DataPlans])}</Box>
                </Stack>
              }
            </Stack>
            <Divider />
            <Box
              sx={{
                paddingTop: 1,
                paddingBottom: 1,
                paddingLeft: 2
              }}>
              <Link
                href="https://help.infegy.com/en/articles/9882798-user-permissions-sharing-capabilities?reload"
                target="_blank"
                sx={{
                  display: "block"
                }}
              >
                {i18n.t("profile-page.user-permissions-and-sharing")}
              </Link>
            </Box>
          </Paper>
          <Paper sx={{ padding: 0, width: "100%"}}>
            <Accordion defaultExpanded={true}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    fontSize: "1.5em",
                    fontWeight: "bold"
                  }}>{i18n.t("profile-page.preferences-heading")}</Box>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <Box sx={{display: "grid", columnGap: 1, rowGap: 1, gridAutoRows: "1fr", gridTemplateColumns: "1fr 2fr"}}>
                  <Box sx={{alignItems: "center", display: "flex", justifyContent: "flex-end"}}>
                    {i18n.t("profile-page.language")}
                  </Box>
                  <Box>
                    <LanguageAutocomplete locale={user.getPreference(UserPreference.LOCALE)} onChange={handleLanguageChange} />
                  </Box>
                  <Box sx={{alignItems: "center", display:"flex", justifyContent: "flex-end"}}>
                    {i18n.t("profile-page.theme")}
                  </Box>
                  <Box>
                    <ThemeAutocomplete themeDefault={user?.getPreference(UserPreference.THEME)} onChange={handleThemeChange} />
                  </Box>
                  <Box sx={{alignItems: "center", display:"flex", justifyContent: "flex-end"}}>
                    {i18n.t("profile-page.time-zone")}
                  </Box>
                  <Box>
                    <TimezoneAutocomplete locale={user.getPreference(UserPreference.LOCALE)} timeZone={user.preferences.time_zone} onChange={handleTimeZoneChange} />
                  </Box>
                  {homepageOptions?.length > 2 && (<>
                  <Box sx={{alignItems: "center", display:"flex", justifyContent: "flex-end"}}>
                    {i18n.t("profile-page.default-home-page")}
                  </Box>
                  <Box>
                    <Select
                      value={defaultHomepage || "auto"}
                      label="Role"
                      onChange={async (event: SelectChangeEvent) => {
                        const page = event.target.value === "auto" ? "" : event.target.value;
                        user.setPreference(UserPreference.DEFAULT_HOMEPAGE, page);
                        setDefaultHomepage(page);
                        await user.savePreferences();
                        enqueueSnackbar(i18n.t("profile-page.default-quicksearch-vs-ai-saved"), { variant: "success" });
                      }}
                    >
                      {homepageOptions}
                    </Select>
                  </Box>
                  </>)}
                  <Box sx={{alignItems: "center", display:"flex", justifyContent: "flex-end"}}>
                    {i18n.t("profile-page.two-factor-auth")}
                  </Box>
                  <Box>
                    <TwoFactorAuthAutocomplete method={user.second_factor} onChange={handleTwoFactorChange} />
                  </Box>
                </Box>
                {hasAdvancedOptions &&
                  <>
                    <Button
                      variant="text"
                      sx={{mt:2}}
                      onClick={() => {
                        setAdvancedOptionsOpen(!advancedOptionsOpen);
                      }} >
                      {advancedOptionsOpen ? i18n.t("dashboard.hide-advanced-options") : i18n.t("dashboard.show-advanced-options")}
                    </Button>
                    {advancedOptionsOpen && (<>
                      <Box sx={{display: "grid", columnGap: 1, rowGap: 1, gridTemplateColumns: "1fr 2fr", gridAutoRows: "1fr"}}>
                        {permissionTest("ai_features") && (<>
                          <Box sx={{alignItems:"center", display: "flex", justifyContent: "flex-end", textAlign: "right"}}>
                            {i18n.t("profile-page.default-quicksearch-vs-ai")}
                          </Box>
                          <Box>
                            <DefaultQuickSearchVsAISelect value={user.getPreference(UserPreference.DEFAULT_QUICKSEARCH_VS_AI)} onChange={handleDefaultQuickSearchVsAIChange} />
                          </Box>
                        </>)}
                        <Box sx={{alignItems: "center", display: "flex", justifyContent: "flex-end", textAlign: "right"}}>
                          {i18n.t("profile-page.skip-converting-quicksearch")}
                        </Box>
                        <Box>
                          <SkipConvertingQuickSearchSelect value={user.getPreference(UserPreference.SKIP_CONVERTING_QUICKSEARCH_BOOLEAN)} onChange={handleSkipConvertingQuickSearchChange} />
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          sx={{ displayPrint: 'none', m: 1}}
                          size="small"
                          onClick={() => {
                            const activeUser = currentUser();
                            const preferences = activeUser?.preferences || {};
                            preferences.default_dashboard = null;
                            APIRequest(`obj/user/${activeUser?.id}`, {}, {
                              method: "PUT",
                              "body": JSON.stringify({
                                "preferences": preferences
                              })
                            }).catch((error: APIError) => {
                              console.error(`Error resetting default dashboard: ${error}`)
                              Sentry.captureException(error);
                            });
                          }}
                        >
                          {i18n.t("profile-page.reset-default-dashboard")}
                        </Button>
                      </Box>
                    </>)}
                  </>
                }
              </AccordionDetails>
            </Accordion>
          </Paper>
          <Paper sx={{ padding: 0, width: "100%"}}>
            <Accordion expanded={isChangePasswordExpanded} onChange={handleToggleChangePasswordExpanded}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    fontSize: "1.5em",
                    fontWeight: "bold"
                  }}>{i18n.t("change-password.heading")}</Box>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <ChangePassword isOpen={isChangePasswordExpanded} user={user} onClose={handleChangePasswordClose} onSuccess={handleChangePasswordSuccess} />
              </AccordionDetails>
            </Accordion>
          </Paper>
          {hasApiPermission &&
            <Paper sx={{ padding: 0, width: "100%"}}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      fontSize: "1.5em",
                      fontWeight: "bold"
                    }}>{i18n.t("manage-api-tokens.heading")}</Box>
                </AccordionSummary>
                <Divider />
                <AccordionDetails>
                  <ManageApiTokens user={user} />
                </AccordionDetails>
              </Accordion>
            </Paper>
          }
          {user.account.subscription_management_link &&
            <Paper sx={{ padding: 2, width: "100%"}}>
              <Link href={user.account.subscription_management_link} variant="h4" target="_blank">{i18n.t("profile-page.manage-your-subscription")}</Link>
            </Paper>
          }
        </Stack>
      </Box>
    </Box>
  );
}